import styled, { css } from 'styled-components';
import { device } from '../../styles/devices';
import Check from '../../assets/icons/Check';
import Info from '../../assets/icons/InfoIcon';

import type { ThemeProps, VehicleHistoryReports } from '../../types';

type ReportProps = {
  report?: VehicleHistoryReports[] | undefined | null;
  cpo?: string | undefined | null;
};

export const CPOInfo = styled(Info)`
  color: #009900;
`;

export const CPOCheck = styled(Check)`
  color: #009900;
`;

const VehicleDetailsPageWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  @media ${device.mobileM} {
    width: 100%;
    margin: 0 auto;
  }
  @media ${device.tablet} {
    width: calc(100% - 120px);
    margin: 0 auto;
  }
  @media ${device.laptopL} {
    width: calc(100% - 192px);
  }
  @media ${device.desktop} {
    max-width: 1248px;
  }
`;

export const Container = styled(VehicleDetailsPageWrapper)`
  background-color: #f2f2f2;
  padding: 60px 18px;
  font-family: 'Audi Type';
  width: 100%;
  ${({ report }: { report: boolean | undefined }) => {
    if (report !== true)
      return css`
        display: flex;
        flex-direction: column;
        align-items: center;
      `;
    return css``;
  }}
  @media ${device.tablet} {
    background-color: #ffffff;
    margin: 0 auto 100px auto;
    width: calc(100% - 120px);
    padding: 0px;
  }
  @media ${device.laptopM} {
    margin: 0 auto 150px auto !important;
  }
  @media ${device.laptopL} {
    width: calc(100% - 192px);
  }
  @media ${device.desktop} {
    max-width: 1248px;
  }
  ${({ report }) => {
    if (report === false || report === undefined)
      return css`
        display: none;
        margin: 0px;
      `;
    return css``;
  }}
`;

export const HeaderContainer = styled.div`
  width: 100%;
  ${({ report, cpo }: ReportProps) => {
    if (report !== null && cpo === null)
      return css`
        text-align: center;
      `;
    return css``;
  }}
  @media ${device.tablet} {
    ${({ report }: ReportProps) => {
      if (report !== null)
        return css`
          text-align: left;
        `;
      return css``;
    }}
  }
`;

export const CPOContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ report }: ReportProps) => {
    if (report !== null)
      return css`
        align-items: center;
      `;
    return css``;
  }}
  @media ${device.tablet} {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

export const InnerContainer = styled.div`
  width: 100%;
  max-width: 868px;
  margin-bottom: 60px;
  ${({ report }: ReportProps) => {
    if (report)
      return css`
        margin-bottom: 0px;
      `;
    return css``;
  }}
  @media ${device.tablet} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }
  @media ${device.laptop} {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
  }
  @media ${device.laptopL} {
    width: 33.3%;
    align-items: center;
    flex-direction: column;
  }
`;

export const H3 = styled.h3`
  font-family: 'Audi Type Extended';
  font-size: 28px;
  line-height: 44px;
  font-weight: 500;
  display: none;
  @media ${device.tablet} {
    display: block;
  }
  @media ${device.laptop} {
    font-size: 36px;
  }
  @media ${device.laptopL} {
    font-size: 40px;
  }
`;

export const RowContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 50px;
  @media ${device.tablet} {
    flex-direction: row;
    min-width: 586px;
  }
  @media ${device.laptopL} {
    width: calc(66.6% - 48px);
  }
`;

export const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
  max-width: 868px;
  min-width: 278px;
  border-bottom: 1px solid #4c4c4c;
  @media ${device.tablet} {
    max-width: 868px;
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  @media ${device.tablet} {
    width: 50%;
    margin-right: 24px;
  }
`;

export const RowLeft = styled.div`
  display: flex;
  align-items: center;
`;

export const ImportantRow = styled(Row)`
  font-size: 21px;
  line-height: 24px;
  font-weight: 700;
  width: 100%;
  max-width: 100%;
  height: auto;
  border-bottom: none !important;
  @media ${device.tablet} {
    font-size: 18px;
    height: auto;
    margin-top: 18px;
  }
`;

export const Model = styled.p`
  font-size: 13px;
  margin-top: 12px;
  line-height: 24px;
  font-weight: 700;
  ${({ report, cpo }: ReportProps) => {
    if (report !== null && cpo === null)
      return css`
        text-align: center;
      `;
    if (cpo !== null && report !== null)
      return css`
        text-align: left;
      `;
    return css``;
  }}
  @media ${device.tablet} {
    ${({ report, cpo }: ReportProps) => {
      if (report !== null)
        return css`
          text-align: left;
          margin-top: 18px;
        `;
      if (cpo !== null)
        return css`
          text-align: left;
        `;
      return css``;
    }}
  }
`;

export const ModelName = styled.span`
  color: ${(props: { theme: ThemeProps }) =>
    props.theme.sys.color.surface.interactive.primary[200]};
`;

export const Feature = styled.div`
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
`;

export const Icon = styled.div`
  width: 35px;
  height: 35px;
  margin-right: 40px;
  display: flex;
  align-items: center;
`;

export const IconDiv = styled.div`
  width: 48px;
  height: 45px;
  margin-right: 40px;
  display: flex;
  align-items: center;
  svg {
    width: 100%;
    max-width: 100%;
    height: 100%;
  }
`;

export const IconImg = styled.img`
  width: 100%;
  max-width: 100%;
  height: 100%;
`;

export const RowRight = styled(RowLeft)`
  display: flex;
  align-items: center;
  ${Feature} {
    margin-right: 12px;
    font-size: 12px;
    line-height: 24px;
    color: #009900;
    font-weight: 400;
  }
  ${Icon} {
    margin-right: 0px;
    width: 12px;
    height: 10px;
  }
`;

export const CarfaxTextLink = styled.p`
  ${({ cpo }: ReportProps) => {
    return cpo
      ? css`
          text-align: center;
        `
      : css`
          text-align: left;
        `;
  }};
  color: ${(props: { theme: ThemeProps }) => props.theme.sys.color.surface.neutral[100]};
  margin-top: 8px;
  @media ${device.mobileS} {
    text-align: center;
  }

  @media ${device.tablet} {
    text-align: left;
  }
  @media ${device.laptopL} {
    ${({ cpo }: ReportProps) => {
      return cpo
        ? css`
            text-align: center;
          `
        : css`
            text-align: left;
          `;
    }};
  }
`;

export const CarfaxContainer = styled.a`
  display: block;
  width: 175px;
  margin: 0 auto 24px auto;
  ${({ report }: ReportProps) => {
    if (report !== null)
      return css`
        margin: 25px auto;
      `;
    return css``;
  }}
  @media ${device.tablet} {
    ${({ report, cpo }: ReportProps) => {
      if (report !== null && cpo === null)
        return css`
          margin: 25px 0 12px;
          align-self: flex-start;
        `;
      return css``;
    }}
    ${({ report, cpo }: ReportProps) => {
      if (report !== null && cpo !== null)
        return css`
          margin: 25px 0 12px;
        `;
      return css``;
    }}
  }
  @media ${device.laptopL} {
    ${({ report, cpo }: ReportProps) => {
      if (report !== null && cpo === null)
        return css`
          margin: 25px 0;
        `;
      return css``;
    }}
    ${({ report, cpo }: ReportProps) => {
      if (report !== null && cpo !== null)
        return css`
          margin: 25px 0;
        `;
      return css``;
    }}
  }
`;

export const CarfaxLogo = styled.img`
  width: 100%;
`;

export const Price = styled.p`
  font-weight: 700;
`;

export const Legal = styled.p`
  margin: 15px 0 60px;
  font-size: 13px;
  line-height: 18px;
  @media ${device.tablet} {
    margin: 61px 0 60px;
  }
`;

export const Estimate = styled.p`
  margin-bottom: 42px;
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
  text-align: center;
`;

export const CtaContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  @media ${device.tablet} {
    flex-direction: row;
    justify-content: flex-end;
  }
  @media ${device.laptopL} {
    flex-direction: column;
  }
`;

export const Cta = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ primary, theme }: { primary?: boolean; theme: ThemeProps }) =>
    primary ? theme.sys.color.surface.neutral[100] : 'transparent'};
  margin-bottom: 12px;
  height: 52px;
  width: 266px;
  color: ${({ primary, theme }: { primary?: boolean; theme: ThemeProps }) =>
    primary ? theme.sys.color.surface.neutral[800] : theme.sys.color.surface.neutral[100]};
  border: ${({ primary, theme }: { primary?: boolean; theme: ThemeProps }) =>
    primary
      ? `1px solid ${theme.sys.color.surface.neutral[100]}`
      : `1px solid ${theme.sys.color.surface.neutral[100]}`};
  text-align: center;
  font-size: 16px;
  &:first-child {
    @media ${device.tablet} {
      margin-right: 8px;
    }
    @media ${device.laptopL} {
      margin-right: 0;
    }
  }
  ${({ primary }) =>
    !primary &&
    `
    &:hover {
      transition: border 1s, color 1s;
      border: 1px solid #808080;
      color: #4c4c4c;
    }
  `}
`;

export const CTABtnContainer = styled.div`
  a {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 12px;
    height: 52px;
    width: 266px;
    text-align: center;
    font-size: 16px;
    padding: 0;
    &:first-child {
      @media ${device.tablet} {
        margin-right: 8px;
      }
      @media ${device.laptopL} {
        margin-right: 0;
      }
    }
  }
`;

export const SkeletonWrapper = styled.div`
  .react-loading-skeleton {
    height: 1023px;
  }
  @media ${device.tablet} {
    .react-loading-skeleton {
      height: 614px;
    }
  }
  @media ${device.laptop} {
    .react-loading-skeleton {
      height: 550px;
    }
  }
  @media ${device.laptopL} {
    .react-loading-skeleton {
      height: 458px;
    }
  }
`;
